/**
 * @file labelPalette.js
 * @description The lead-labels ("תוויות") colour identity - the ONE file that
 * decides what each stored label colour looks like, everywhere it is drawn:
 * the ears above a card, the chip row, the dots in the panel and the manage
 * dialog, the swatches, and the recoloured card banner of a label view.
 * Every colour DOT is drawn with `dotGradient` - never a flat colour.
 *
 * Framework-agnostic: plain values only, no MUI imports (mirrors tokens.js and
 * leadPalette.js).
 *
 * THE KEYS ARE DATA. `LABEL_COLOR_KEYS` is what the backend stores in
 * `lead_labels.color`, in a FIXED order (the swatch order). Never rename or
 * reorder a key; a new colour is appended.
 *
 * THE COLOURS ARE THE OWNER'S CHOICE: the mockup's set "a" (flat finish, tab
 * outline), kept 2026-09-29; the alternative sets it offered are gone.
 * Consumers only ever call `labelColor(key)` / read `LABEL_MORE_EAR`, so a
 * colour change is still a values-only change in `BASE` below.
 *
 * GRADIENT RULE (owner, fixed - not a reading-direction rule): every gradient
 * in an ear or a bar runs DARK on the physical RIGHT and BRIGHT on the physical
 * LEFT, and is NEVER mirrored under dir="rtl". CSS gradients are physical, so
 * the angle alone decides it: `270deg` (=== "to left") starts its line at the
 * RIGHT edge, so the 0% stop - always the DARK one here - paints the right and
 * the 100% stop - always the BRIGHT one - paints the left.
 * labelPalette.test.js locks that for every colour.
 */

/** The stored colour keys, in the fixed swatch order. */
export const LABEL_COLOR_KEYS = Object.freeze([
  "turquoise",
  "sky",
  "lavender",
  "pink",
  "coral",
  "amber",
  "green",
  "slate",
  "indigo",
  "bordeaux",
]);

/** The key an unknown/legacy value falls back to - the neutral one. */
export const FALLBACK_LABEL_COLOR = "slate";

// "to left": the gradient line starts at the physical RIGHT edge. The 0% stop
// is therefore the right side - always DARK below; 100% is the left - BRIGHT.
const PHYSICAL_RIGHT_TO_LEFT = "270deg";

// ---------------------------------------------------------------------------
// The swatch values (the mockup's `--lN-*` tokens, set "a").
//   dot  - ears' middle stop, dots, swatch circles (saturated)
//   fill - active chip background / banner DARK end (pastel-mid)
//   tint - banner BRIGHT end (very light)
//   ink  - readable text on fill or tint (>= 4.5:1 on both)
//   deep - active chip border; the ear's DARK stop
//   hi   - the ear's BRIGHT stop
// Light pink (`pink`) is deliberately pastel - it must read as LIGHT pink.
// ---------------------------------------------------------------------------
const BASE = {
  turquoise: { dot: "#14B8A6", fill: "#A7EDE3", tint: "#E8FAF7", ink: "#0F5F58", deep: "#0D9488", hi: "#6EE7D8" },
  sky:       { dot: "#38A8E8", fill: "#B9E2F8", tint: "#EAF6FD", ink: "#0B5A86", deep: "#0284C7", hi: "#8FD6FA" },
  lavender:  { dot: "#8B7CF6", fill: "#D6D0FD", tint: "#F2F0FE", ink: "#4638A6", deep: "#6D5DE8", hi: "#BBB2FB" },
  pink:      { dot: "#F4A3C4", fill: "#FBD3E3", tint: "#FEF0F5", ink: "#9A2E5C", deep: "#E26E9C", hi: "#F9C6DB" },
  coral:     { dot: "#F28160", fill: "#FCD0C2", tint: "#FEF1EC", ink: "#8A3517", deep: "#E4572E", hi: "#F9AD94" },
  amber:     { dot: "#F2B230", fill: "#FBE3A8", tint: "#FEF7E6", ink: "#7A4F05", deep: "#D99A12", hi: "#F8D175" },
  green:     { dot: "#3FBF7F", fill: "#BDEBD2", tint: "#EBF8F1", ink: "#13603C", deep: "#16A34A", hi: "#86DDAE" },
  slate:     { dot: "#7C8CA3", fill: "#D5DCE6", tint: "#F1F4F8", ink: "#334155", deep: "#5B6B82", hi: "#AEB9C8" },
  // Deep indigo: darker and bluer than lavender, saturated where slate is grey.
  indigo:    { dot: "#4F46E5", fill: "#A9B1F0", tint: "#EEEFFC", ink: "#2E2A7E", deep: "#3730A3", hi: "#7F86EE" },
  // Bordeaux (wine red): far darker than coral, blue-red where pink is pastel.
  bordeaux:  { dot: "#9B1B3C", fill: "#D89CAD", tint: "#F9EBEF", ink: "#5C0B22", deep: "#6E0F28", hi: "#C4465F" },
};

// Each colour's ear runs deep -> dot -> hi; the "+N" ear is its own neutral.
const EAR_STOPS = [0, 52, 100];
const active = Object.freeze({
  stops: EAR_STOPS,
  colors: Object.fromEntries(
    LABEL_COLOR_KEYS.map((key) => {
      const b = BASE[key];
      return [
        key,
        {
          dot: b.dot,
          fill: b.fill,
          tint: b.tint,
          ink: b.ink,
          deep: b.deep,
          earStops: [b.deep, b.dot, b.hi],
        },
      ];
    })
  ),
  more: ["#8D97A6", "#AFB7C3", "#D3D8DF"],
  moreInk: "#fff",
});

/** `#RRGGBB` a..b at `t` (0 = a, 1 = b). */
function mixHex(a, b, t) {
  const pa = parseInt(a.slice(1), 16);
  const pb = parseInt(b.slice(1), 16);
  const ch = (shift) => {
    const va = (pa >> shift) & 255;
    const vb = (pb >> shift) & 255;
    return Math.round(va + (vb - va) * t);
  };
  const out = (ch(16) << 16) | (ch(8) << 8) | ch(0);
  return `#${out.toString(16).padStart(6, "0").toUpperCase()}`;
}

// A dot is 7-12px: the ear's full spread would wash its bright end out and
// read as a different colour, so the dot's bright stop is pulled halfway back
// towards the mid, and the mid sits in the centre.
const DOT_STOPS = [0, 50, 100];
const dotStops = ([dark, mid, bright]) => [dark, mid, mixHex(mid, bright, 0.5)];

/**
 * A dark-right / bright-left gradient over `[dark, mid, bright]` at `stops`.
 * Physical angle - never mirrored by direction (see the file header).
 */
export function darkRightGradient([dark, mid, bright], [p0, p1, p2]) {
  return `linear-gradient(${PHYSICAL_RIGHT_TO_LEFT}, ${dark} ${p0}%, ${mid} ${p1}%, ${bright} ${p2}%)`;
}

const RESOLVED = Object.freeze(
  Object.fromEntries(
    LABEL_COLOR_KEYS.map((key) => {
      const c = active.colors[key];
      return [
        key,
        Object.freeze({
          key,
          dot: c.dot,
          fill: c.fill,
          tint: c.tint,
          ink: c.ink,
          deep: c.deep,
          // The ear's flat tab gradient.
          earGradient: darkRightGradient(c.earStops, active.stops),
          // Every colour DOT (chip row, panel rows, manage dialog, swatches,
          // header chips, starters, the label picker): the same colour as a
          // small gradient, tightened so it still reads as that colour.
          dotGradient: darkRightGradient(dotStops(c.earStops), DOT_STOPS),
          // The recoloured card banner of a label view (variant B). Same SHAPE
          // as LeadCard's own banner (`linear-gradient(to left, a 0%, b 100%)`),
          // so it is a drop-in `background` value: fill (darker) on the
          // physical right, tint (brighter) on the physical left.
          bannerGradient: `linear-gradient(${PHYSICAL_RIGHT_TO_LEFT}, ${c.fill} 0%, ${c.tint} 100%)`,
        }),
      ];
    })
  )
);

/** True when `key` is one of the stored colour keys. */
export const isLabelColor = (key) =>
  typeof key === "string" && Object.prototype.hasOwnProperty.call(RESOLVED, key);

/**
 * Everything a surface needs to draw one label colour. An unknown key (a
 * colour a newer backend added, a corrupted row) falls back to the neutral
 * slate - never a crash, never an invisible label.
 *
 * @param {string} key a `LABEL_COLOR_KEYS` value.
 * @returns {{key: string, dot: string, fill: string, tint: string, ink: string,
 *   deep: string, earGradient: string, dotGradient: string, bannerGradient: string}}
 */
export function labelColor(key) {
  return isLabelColor(key) ? RESOLVED[key] : RESOLVED[FALLBACK_LABEL_COLOR];
}

/** The neutral "+N" ear: its gradient and its text colour. */
export const LABEL_MORE_EAR = Object.freeze({
  earGradient: darkRightGradient(active.more, active.stops),
  ink: active.moreInk,
});

/**
 * The options menu's "תוויות" row: its three-dot glyph (amber, sky, pink - the
 * mockup's `.dots3`, as gradient dots) and its own multi-hue wash - DARKER teal end on the
 * physical RIGHT, brighter rose end on the physical LEFT.
 */
export const LABELS_ROW_TONE = Object.freeze({
  // Gradient dots, like every other label dot (a CSS `background` value).
  dots: Object.freeze([
    RESOLVED.amber.dotGradient,
    RESOLVED.sky.dotGradient,
    RESOLVED.pink.dotGradient,
  ]),
  color: "#1f4f4a",
  pressedColor: "#0f4f49",
  bg:`linear-gradient(${PHYSICAL_RIGHT_TO_LEFT}, rgba(20,184,166,0.13) 0%, rgba(139,124,246,0.09) 55%, rgba(244,163,196,0.11) 100%)`,
  hoverBg: `linear-gradient(${PHYSICAL_RIGHT_TO_LEFT}, rgba(20,184,166,0.22) 0%, rgba(139,124,246,0.15) 55%, rgba(244,163,196,0.18) 100%)`,
  pressedBg: `linear-gradient(${PHYSICAL_RIGHT_TO_LEFT}, rgba(20,184,166,0.32) 0%, rgba(139,124,246,0.20) 55%, rgba(244,163,196,0.24) 100%)`,
  border: "rgba(20,184,166,0.28)",
  hoverBorder: "rgba(20,184,166,0.5)",
  pressedBorder: "rgba(13,148,136,0.75)",
  pressedShadow: "inset 0 1px 2px rgba(13,148,136,0.22)",
});
